import React, { useMemo, useState } from "react";
import { collection, getDocs } from "firebase/firestore";
import { RiSearchLine } from "react-icons/ri";
import { db } from "../firebase/firebase";
import ButtonComponent from "../ButtonComponent/ButtonComponent";
import "./TableSearch.scss";

const TableSearch = ({ slug = null, weddingData = null }) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]);
  const [hasSearched, setHasSearched] = useState(false);
  const [loading, setLoading] = useState(false);

  const ceremonyTypeRaw = weddingData?.ceremony_type || "wedding";

  const isHomeComing = useMemo(() => {
    const normalized = String(ceremonyTypeRaw).trim().toLowerCase();
    return (
      normalized === "home coming" ||
      normalized === "homecoming" ||
      normalized === "home-coming"
    );
  }, [ceremonyTypeRaw]);

  const badgeText = isHomeComing ? "Homecoming Seating" : "Wedding Seating";

  const headingText = "Find Your Table";

  const subheadingText = isHomeComing
    ? "Search your name below to see your assigned table number for the homecoming."
    : "Search your name below to see your assigned table number.";

  const loadingText = "Searching for your table...";

  const pendingText = "Your table assignment is not ready yet";

  const tableLabelText = "Your Table No. is";

  const emptyText =
    "Please check the spelling or make sure you have confirmed your participation.";

  const normalizeText = (value) =>
    String(value || "")
      .toLowerCase()
      .trim()
      .replace(/\s+/g, "");

  const handleSearch = async () => {
    const raw = searchQuery.trim();

    if (!raw) {
      setSearchResults([]);
      setHasSearched(false);
      return;
    }

    if (!slug) {
      setSearchResults([]);
      setHasSearched(true);
      return;
    }

    setLoading(true);
    setHasSearched(true);

    try {
      const groomGuestsRef = collection(db, "sites", slug, "GroomGuests");
      const brideGuestsRef = collection(db, "sites", slug, "BrideGuests");

      const [groomSnapshot, brideSnapshot] = await Promise.all([
        getDocs(groomGuestsRef),
        getDocs(brideGuestsRef),
      ]);

      const q = normalizeText(raw);

      const processGuests = (docs, collectionName) =>
        docs
          .flatMap((docSnap) => {
            const data = docSnap.data() || {};
            const guests = Array.isArray(data.guests) ? data.guests : [];

            return guests.map((guest, index) =>
              guest?.name
                ? {
                    id: guest.id || `${docSnap.id}-${index}`,
                    name: guest.name,
                    seatNumber: guest.seatNumber || "",
                    status: guest.status || "",
                    documentId: docSnap.id,
                    collectionName,
                  }
                : null
            );
          })
          .filter(Boolean);

      const groomGuests = processGuests(groomSnapshot.docs, "GroomGuests");
      const brideGuests = processGuests(brideSnapshot.docs, "BrideGuests");

      const matchedGuests = [...groomGuests, ...brideGuests].filter((guest) =>
        normalizeText(guest.name).includes(q)
      );

      const validGuests = matchedGuests.filter(
        (guest) => guest.status !== "Not Participating"
      );

      setSearchResults(validGuests);
    } catch (error) {
      console.error("Error fetching guests:", error);
      setSearchResults([]);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section className="ts">
      <div className="ts__shell">
        <div className="ts__card">
          <div className="ts__header">
            <div className="ts__badge">{badgeText}</div>

            <h1 className="ts__heading">{headingText}</h1>

            <p className="ts__subheading">{subheadingText}</p>
          </div>

          <div className="ts__searchWrap">
            <div className="ts__search">
              <div className="ts__inputWrap">
                <RiSearchLine className="ts__inputIcon" />
                <input
                  type="text"
                  className="ts__input"
                  placeholder="Enter your name"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      handleSearch();
                    }
                  }}
                  aria-label="Guest name"
                  autoCapitalize="words"
                  autoCorrect="off"
                />
              </div>

              <ButtonComponent onClick={handleSearch}>Search</ButtonComponent>
            </div>
          </div>

          <div className="ts__results">
            {loading && <p className="ts__note">{loadingText}</p>}

            {!loading && hasSearched && searchResults.length === 0 && (
              <div className="ts__empty">
                <p className="ts__emptyTitle">No guest found</p>
                <p className="ts__emptyText">{emptyText}</p>
              </div>
            )}

            {!loading && searchResults.length > 0 && (
              <>
                <div className="ts__resultsHeader">
                  <p className="ts__resultsCount">
                    {searchResults.length} result
                    {searchResults.length > 1 ? "s" : ""} found
                  </p>
                </div>

                {searchResults.map((res) => (
                  <div
                    key={`${res.collectionName}-${res.documentId}-${res.id}`}
                    className="ts__item"
                  >
                    <div className="ts__itemInner">
                      <div className="ts__guest">
                        <span className="ts__label">Guest</span>
                        <p className="ts__name">{res.name}</p>
                      </div>

                      <div className="ts__tablePill">
                        {res.status === "Pending" ? (
                          <span className="ts__tableLabel">{pendingText}</span>
                        ) : (
                          <>
                            <span className="ts__tableLabel">
                              {tableLabelText}
                            </span>
                            <span className="ts__tableNumber">
                              {res.seatNumber || "—"}
                            </span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default TableSearch;